import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Recycle, Flame, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const WasteToEnergy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/waste-to-energy"]?.title} description={seoData["industries/waste-to-energy"]?.description} path="/industries/waste-to-energy" keywords={seoData["industries/waste-to-energy"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Waste-to-Energy"
        description="Coating and repair for waste-to-energy plants. Corrosion and erosion protection for boilers, scrubbers, material handling systems, and ash handling equipment."
        backgroundKey="waste-to-energy"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Boiler tube corrosion from acidic flue gases and high chloride content',
              'Scrubber lining failure from HCl and SOx exposure',
              'Refuse feed chute and hopper erosion',
              'Ash handling system wear and corrosion',
              'Cooling water system deterioration',
              'Stack and ductwork corrosion from condensate'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-lime-50 rounded-lg">
                <Recycle className="text-lime-700 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-lime-700">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Flame className="text-lime-700 mr-3 flex-shrink-0" size={22} />
                  Boiler & Flue Gas System Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">High-temperature, acid-resistant coating systems for boiler waterwall tubes, economizers, and flue gas ductwork. Protection against the aggressive combustion byproducts of municipal solid waste.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-lime-700">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Flame className="text-lime-700 mr-3 flex-shrink-0" size={22} />
                  Scrubber & FGD Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Chemical-resistant lining systems for wet and dry scrubbers, quench towers, and FGD absorbers. Vinyl ester and epoxy systems rated for continuous acid exposure.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-lime-700">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Flame className="text-lime-700 mr-3 flex-shrink-0" size={22} />
                  Material Handling Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Abrasion-resistant coatings for refuse feed chutes, hoppers, conveyor systems, and ash handling equipment. Ceramic-filled and rubber-based systems for extreme wear.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-lime-700">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Flame className="text-lime-700 mr-3 flex-shrink-0" size={22} />
                  Cooling & Utility System Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Pipe repair, condenser coating, and cooling tower restoration for plant utility systems. Corrosion protection for condensate return and feedwater piping.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Waste-to-Energy?</h2>
              <div className="space-y-3">
                {['Combined corrosion and erosion protection for WTE environments',
                  'Acid and chloride-resistant coating systems',
                  'Outage-optimized scheduling and fast-cure systems',
                  'Confined space and high-temperature work certified crews',
                  'On-site blasting and surface preparation',
                  'Full QA/QC documentation and warranty'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-lime-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">WTE Plant Specialists</h3>
              <p className="text-lime-100 mb-6">BCS understands the unique combination of corrosion, erosion, and high-temperature challenges in waste-to-energy facilities. Purpose-built coating solutions for every plant area.</p>
              <Button className="bg-white text-View High-Temp Coatings hover:bg-gray-100" onClick={() => navigate('/services/protective-coatings')}>
                View High-Temp Coatings <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your WTE Plant</h2>
          <p className="text-blue-100 mb-8">Contact us to plan coating work around your next scheduled outage.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WasteToEnergy;
